import { useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { Pencil, Trash2, Plus, StickyNote } from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { useSession } from '@/hooks/useSession';
import {
  useAccountNotes,
  useCreateAccountNote,
  useUpdateAccountNote,
  useDeleteAccountNote,
} from '../hooks/useAccountNotes';
import type { AccountNote } from '../types';

const PAGE_SIZE = 20;

const CATEGORY_OPTIONS = [
  { value: 'general', label: 'General' },
  { value: 'follow_up', label: 'Follow-up' },
  { value: 'internal', label: 'Internal' },
  { value: 'issue', label: 'Issue' },
];

const CATEGORY_STYLES: Record<string, string> = {
  general: 'bg-gray-100 text-gray-700',
  follow_up: 'bg-blue-100 text-blue-700',
  internal: 'bg-purple-100 text-purple-700',
  issue: 'bg-red-100 text-red-700',
};

const CATEGORY_LABELS: Record<string, string> = {
  general: 'General',
  follow_up: 'Follow-up',
  internal: 'Internal',
  issue: 'Issue',
};

export default function NotesTab({ accountId }: { accountId: string }) {
  const [page, setPage] = useState(0);
  const [categoryFilter, setCategoryFilter] = useState<string | undefined>(undefined);
  const [isCreating, setIsCreating] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [content, setContent] = useState('');
  const [category, setCategory] = useState('');

  const { user } = useSession();
  const { data, isLoading, error, refetch } = useAccountNotes(accountId, {
    pageNumber: page,
    pageLength: PAGE_SIZE,
    category: categoryFilter,
  });
  const createMutation = useCreateAccountNote(accountId);
  const updateMutation = useUpdateAccountNote(accountId);
  const deleteMutation = useDeleteAccountNote(accountId);

  const handleCreate = () => {
    if (!content.trim()) return;
    createMutation.mutate(
      { content: content.trim(), category: category || undefined },
      {
        onSuccess: () => {
          setIsCreating(false);
          setContent('');
          setCategory('');
          setPage(0);
        },
      }
    );
  };

  const handleUpdate = () => {
    if (!editingNoteId || !content.trim()) return;
    updateMutation.mutate(
      { noteId: editingNoteId, data: { content: content.trim(), category: category || undefined } },
      {
        onSuccess: () => {
          setEditingNoteId(null);
          setContent('');
          setCategory('');
        },
      }
    );
  };

  const startEditing = (note: AccountNote) => {
    setEditingNoteId(note.id);
    setContent(note.content);
    setCategory(note.category || '');
    setIsCreating(false);
  };

  const cancelForm = () => {
    setIsCreating(false);
    setEditingNoteId(null);
    setContent('');
    setCategory('');
  };

  if (isLoading) {
    return <NotesTabSkeleton />;
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-10 text-center space-y-4">
          <p className="text-destructive">Error loading notes</p>
          <p className="text-sm text-muted-foreground">
            {error instanceof Error ? error.message : 'An unexpected error occurred'}
          </p>
          <Button variant="outline" onClick={() => refetch()}>
            Try Again
          </Button>
        </CardContent>
      </Card>
    );
  }

  const notes = data?.notes || [];
  const total = data?.total || 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CardTitle>Notes</CardTitle>
            <Badge variant="secondary">{total}</Badge>
          </div>
          <div className="flex items-center gap-2">
            <Select
              value={categoryFilter ?? 'all'}
              onValueChange={(v) => {
                setCategoryFilter(v === 'all' ? undefined : v);
                setPage(0);
              }}
            >
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {CATEGORY_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              size="sm"
              onClick={() => {
                setIsCreating(true);
                setEditingNoteId(null);
                setContent('');
                setCategory('');
              }}
              disabled={isCreating}
            >
              <Plus className="h-4 w-4 mr-1" />
              Add Note
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* New note form */}
        {isCreating && (
          <div className="mb-4 p-4 border rounded-lg space-y-3">
            <Textarea
              placeholder="Write your note..."
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={4}
            />
            <div className="flex items-center justify-between">
              <Select value={category || 'none'} onValueChange={(v) => setCategory(v === 'none' ? '' : v)}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Category</SelectItem>
                  {CATEGORY_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={cancelForm}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleCreate}
                  disabled={!content.trim() || createMutation.isPending}
                >
                  {createMutation.isPending ? 'Saving...' : 'Save'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Notes list */}
        {notes.length === 0 && !isCreating ? (
          <div className="py-8 text-center text-muted-foreground">
            <StickyNote className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p>No notes yet. Add the first note for this account.</p>
          </div>
        ) : (
          <>
            <div className="space-y-1">
              {notes.map((note) =>
                editingNoteId === note.id ? (
                  <div key={note.id} className="p-4 border rounded-lg space-y-3">
                    <Textarea
                      value={content}
                      onChange={(e) => setContent(e.target.value)}
                      rows={4}
                    />
                    <div className="flex items-center justify-between">
                      <Select value={category || 'none'} onValueChange={(v) => setCategory(v === 'none' ? '' : v)}>
                        <SelectTrigger className="w-[140px]">
                          <SelectValue placeholder="Category" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No Category</SelectItem>
                          {CATEGORY_OPTIONS.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={cancelForm}>
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          onClick={handleUpdate}
                          disabled={!content.trim() || updateMutation.isPending}
                        >
                          {updateMutation.isPending ? 'Saving...' : 'Save'}
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <NoteItem
                    key={note.id}
                    note={note}
                    isOwner={user?._id === note.created_by_id}
                    onEdit={() => startEditing(note)}
                    onDelete={() => deleteMutation.mutate(note.id)}
                    isDeleting={deleteMutation.isPending}
                  />
                )
              )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t">
                <p className="text-sm text-muted-foreground">
                  Page {page + 1} of {totalPages}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                    disabled={page === 0}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                    disabled={page >= totalPages - 1}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function NoteItem({
  note,
  isOwner,
  onEdit,
  onDelete,
  isDeleting,
}: {
  note: AccountNote;
  isOwner: boolean;
  onEdit: () => void;
  onDelete: () => void;
  isDeleting: boolean;
}) {
  return (
    <div className="flex items-start gap-3 py-3 border-b border-border/50 last:border-0">
      <div className="flex-1 min-w-0">
        <p className="text-sm text-foreground" style={{ whiteSpace: 'pre-wrap' }}>
          {note.content}
        </p>
        <div className="flex items-center gap-2 mt-1">
          <span className="text-xs text-muted-foreground">
            {note.created_by_name}
          </span>
          {note.category && CATEGORY_LABELS[note.category] && (
            <Badge className={`text-xs ${CATEGORY_STYLES[note.category]}`} variant="secondary">
              {CATEGORY_LABELS[note.category]}
            </Badge>
          )}
          <span className="text-xs text-muted-foreground">
            {formatDistanceToNow(new Date(note.created_at), { addSuffix: true })}
          </span>
        </div>
      </div>
      {isOwner && (
        <div className="flex items-center gap-1 shrink-0">
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={onEdit}>
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                disabled={isDeleting}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete Note</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to delete this note? This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={onDelete}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      )}
    </div>
  );
}

function NotesTabSkeleton() {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Skeleton className="h-6 w-20" />
          <Skeleton className="h-5 w-8 rounded-full" />
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="space-y-2">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-3 w-1/3" />
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
